<template>
    <CCard class="mb-4 border-0 shadow-sm admin-filter-card">
        <CCardBody class="p-4">
            <div class="artwork-filter-grid">
                <!-- Search Field -->
                <div>
                    <label for="artwork-filter-search" class="filter-label">{{ $i18n.locale === 'en' ? 'Search Artworks' : 'ค้นหาผลงาน' }}</label>
                    <div class="search-field-wrapper">
                        <CIcon name="cilMagnifyingGlass" class="search-icon-fixed" />
                        <CInput 
                            id="artwork-filter-search"
                            :value="value.search" 
                            @input="updateFilter('search', $event)"
                            class="m-0 border-0 custom-input-plain"
                            :placeholder="$i18n.locale === 'en' ? 'Title, Artist...' : 'ชื่อผลงาน, ศิลปิน...'" 
                        />
                    </div>
                </div>

                <!-- Category/Status Filters -->
                <div>
                    <label class="filter-label">{{ $i18n.locale === 'en' ? 'Category' : 'หมวดหมู่' }}</label>
                    <CDropdown :key="value.category + '_' + $i18n.locale" :caret="false" class="w-100 custom-add-dropdown">
                        <template #toggler>
                            <CButton type="button" class="filter-select-btn">
                                <span class="text-truncate mr-2">{{ getCategoryLabel(value.category) }}</span>
                                <CIcon name="cil-layers" class="filter-icon-small" />
                            </CButton>
                        </template>
                        <div class="w-100 shadow-sm border-0 mt-1">
                        <CDropdownItem @click="updateFilter('category', 'All Categories')">{{ $i18n.locale === 'en' ? 'All Categories' : 'หมวดหมู่ทั้งหมด' }}</CDropdownItem>
                        <CDropdownItem v-for="category in categories" :key="category._id" @click="updateFilter('category', category._id)">{{ getCategoryLabel(category._id) }}</CDropdownItem>
                        <CDropdownItem v-if="!categories.length" disabled>{{ $i18n.locale === 'en' ? 'No categories available' : 'ไม่พบหมวดหมู่' }}</CDropdownItem>
                        </div>
                    </CDropdown>
                </div>

                <div>
                    <label class="filter-label">{{ $i18n.locale === 'en' ? 'Status' : 'สถานะ' }}</label>
                    <CDropdown :key="value.status + '_' + $i18n.locale" :caret="false" class="w-100 custom-add-dropdown">
                        <template #toggler>
                            <CButton type="button" class="filter-select-btn">
                                <span class="text-truncate mr-2">{{ getStatusLabel(value.status) }}</span>
                                <CIcon name="cil-filter" class="filter-icon-small" />
                            </CButton>
                        </template>
                        <div class="w-100 shadow-sm border-0 mt-1">
                        <CDropdownItem @click="updateFilter('status', 'All Status')">{{ $i18n.locale === 'en' ? 'All Status' : 'สถานะทั้งหมด' }}</CDropdownItem>
                        <CDropdownItem @click="updateFilter('status', 'Pending')">{{ $i18n.locale === 'en' ? 'Pending' : 'รอดำเนินการ' }}</CDropdownItem>
                        <CDropdownItem @click="updateFilter('status', 'Approved')">{{ $i18n.locale === 'en' ? 'Approved' : 'อนุมัติแล้ว' }}</CDropdownItem>
                        <CDropdownItem @click="updateFilter('status', 'Rejected')">{{ $i18n.locale === 'en' ? 'Rejected' : 'ปฏิเสธ' }}</CDropdownItem>
                        </div>
                    </CDropdown>
                </div>

                <!-- Quick Range -->
                <div>
                    <label class="filter-label">{{ $i18n.locale === 'en' ? 'Quick Range' : 'ช่วงเวลาด่วน' }}</label>
                    <CButtonGroup class="w-100 quick-range-group">
                        <CButton 
                            v-for="range in [ 'All Time' , 'Today', 'This Month', 'This Year']" 
                            :key="range"
                            @click="setQuickRange(range)"
                            :class="['range-btn', { 'active': value.quickRange === range }]"
                        >
                            {{ getQuickRangeLabel(range) }}
                        </CButton>
                    </CButtonGroup>
                </div>

                <!-- Time Range -->
                <div>
                    <label class="filter-label">{{ $i18n.locale === 'en' ? 'Time Range' : 'ช่วงเวลา' }}</label>
                    <div class="d-flex align-items-center">
                        <div class="date-input-wrapper">
                            <label for="artwork-filter-start" class="sr-only">{{ $i18n.locale === 'en' ? 'Start date' : 'วันเริ่มต้น' }}</label>
                            <CInput 
                                id="artwork-filter-start"
                                :type="formattedStartDate ? 'date' : 'text'"
                                :placeholder="$i18n.locale === 'en' ? 'Start date' : 'วันเริ่มต้น'"
                                lang="en-GB"
                                :value="formattedStartDate" 
                                @focus="$event.target.type = 'date'; if ($event.target.showPicker) $event.target.showPicker();"
                                @blur="!$event.target.value && ($event.target.type = 'text')"
                                @input="onDateInput('startDate', $event)"
                                class="mb-0 custom-date-field" 
                            />
                            <CIcon name="cil-calendar" class="date-icon-right" />
                        </div>
                        <span class="mx-1 text-muted">&ndash;</span>
                        <div class="date-input-wrapper">
                            <label for="artwork-filter-end" class="sr-only">{{ $i18n.locale === 'en' ? 'End date' : 'วันสิ้นสุด' }}</label>
                            <CInput 
                                id="artwork-filter-end"
                                :type="formattedEndDate ? 'date' : 'text'"
                                :placeholder="$i18n.locale === 'en' ? 'End date' : 'วันสิ้นสุด'"
                                lang="en-GB"
                                :value="formattedEndDate" 
                                @focus="$event.target.type = 'date'; if ($event.target.showPicker) $event.target.showPicker();"
                                @blur="!$event.target.value && ($event.target.type = 'text')"
                                @input="onDateInput('endDate', $event)"
                                class="mb-0 custom-date-field" 
                            />
                            <CIcon name="cil-calendar" class="date-icon-right" />
                        </div>
                    </div>
                </div>
            </div>
        </CCardBody>
    </CCard>
</template>

<script>
import moment from 'moment'

export default {
    name: 'ArtworkFilter',
    props: {
        value: {
            type: Object,
            required: true
        },
        categories: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        formattedStartDate() {
            return this.value.startDate ? moment(this.value.startDate).format('YYYY-MM-DD') : ''
        },
        formattedEndDate() {
            return this.value.endDate ? moment(this.value.endDate).format('YYYY-MM-DD') : ''
        }
    },
    methods: {
        getCategoryLabel(category) {
            if (category === 'All Categories') {
                return this.$i18n.locale === 'en' ? 'All Categories' : 'หมวดหมู่ทั้งหมด';
            }
            const selected = this.categories.find(item => String(item._id) === String(category));
            return selected ? (this.getName(this.$i18n.locale, selected) || this.getName('en', selected)) : category;
        },
        getName(lang, item) {
            const title = item && (item.title || item.name);
            if (typeof title === 'string') return title;
            if (!Array.isArray(title)) return '';
            return (title.find(value => value.key === lang) || title[0] || {}).value || '';
        },
        getStatusLabel(status) {
            if (status === 'All Status') {
                return this.$i18n.locale === 'en' ? 'All Status' : 'สถานะทั้งหมด';
            }
            if (status === 'Pending') {
                return this.$i18n.locale === 'en' ? 'Pending' : 'รอดำเนินการ';
            }
            if (status === 'Approved') {
                return this.$i18n.locale === 'en' ? 'Approved' : 'อนุมัติแล้ว';
            }
            if (status === 'Rejected') {
                return this.$i18n.locale === 'en' ? 'Rejected' : 'ปฏิเสธ';
            }
            return status;
        },
        getQuickRangeLabel(range) {
            if (range === 'All Time') {
                return this.$i18n.locale === 'en' ? 'All Time' : 'ทั้งหมด';
            }
            if (range === 'Today') {
                return this.$i18n.locale === 'en' ? 'Today' : 'วันนี้';
            }
            if (range === 'This Month') {
                return this.$i18n.locale === 'en' ? 'This Month' : 'เดือนนี้';
            }
            if (range === 'This Year') {
                return this.$i18n.locale === 'en' ? 'This Year' : 'ปีนี้';
            }
            return range;
        },
        updateFilter(key, val) {
            const newFilters = { ...this.value, [key]: val }
            this.$emit('input', newFilters)
        },
        onDateInput(type, val) {
            const dateVal = val ? moment(val).toDate() : null
            const newFilters = { 
                ...this.value, 
                [type]: dateVal,
                quickRange: 'Custom'
            }
            this.$emit('input', newFilters)
        },
        setQuickRange(rangeType) {
            let start = null
            let end = null

            switch (rangeType) {
                case 'Today':
                    start = moment().startOf('day')
                    end = moment().endOf('day')
                    break
                case 'This Month':
                    start = moment().startOf('month')
                    end = moment().endOf('month')
                    break
                case 'This Year':
                    start = moment().startOf('year')
                    end = moment().endOf('year')
                    break
                case 'All Time':
                default:
                    start = null
                    end = null
                    break
            }

            const newFilters = {
                ...this.value,
                quickRange: rangeType,
                startDate: start ? start.toDate() : null,
                endDate: end ? end.toDate() : null
            }
            this.$emit('input', newFilters)
        }
    }
}
</script>

<style scoped>
.artwork-filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
    gap: 16px;
}

.artwork-filter-grid > div { min-width: 0; }

.admin-filter-card {
    position: relative !important;
    z-index: 5 !important;
    overflow: visible !important;
    border-radius: 12px;
}

.custom-add-dropdown {
    position: relative !important;
}

.filter-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #4f5d73;
    margin-bottom: 8px;
    display: block;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.search-field-wrapper {
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0 12px;
    height: 42px;
    transition: all 0.2s ease;
}

.search-field-wrapper:focus-within {
    background: #ffffff;
    border-color: #321fdb;
    box-shadow: 0 0 0 3px rgba(50, 31, 219, 0.1);
}

.search-icon-fixed {
    color: #8c98a9;
    width: 16px;
    margin-right: 10px;
}

.custom-input-plain ::v-deep .form-group {
    margin-bottom: 0 !important;
}

.custom-input-plain ::v-deep .form-control {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    font-size: 0.9rem;
    height: auto !important;
    color: #2c3e50;
}

.filter-select-btn {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0 16px;
    height: 42px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #44546d;
    transition: all 0.2s ease;
    outline: none;
}

.filter-select-btn:hover {
    background: #ffffff;
    border-color: #321fdb;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.filter-icon-small {
    width: 12px;
    color: #8a93a2;
}

/* Time Range Group */
.date-input-wrapper {
    min-width: 0;
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    height: 42px;
    transition: all 0.2s ease;
    overflow: hidden;
}

.date-input-wrapper:focus-within {
    background: #ffffff;
    border-color: #321fdb;
    box-shadow: 0 0 0 3px rgba(50, 31, 219, 0.1);
}

.date-icon-right {
    position: absolute;
    right: 12px;
    width: 16px;
    color: #8a93a2;
    pointer-events: none;
    z-index: 2;
}

.custom-date-field {
    width: 100%;
    margin-bottom: 0 !important;
}

.custom-date-field ::v-deep .form-control {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-size: 0.85rem;
    font-weight: 500;
    color: #3b4d6b;
    height: 42px;
    text-align: center;
    cursor: pointer;
    padding-left: 8px;
    padding-right: 28px;
}

.custom-date-field ::v-deep .form-control::-webkit-calendar-picker-indicator {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    cursor: pointer;
    opacity: 0;
}

/* Quick Range ButtonGroup */
.quick-range-group {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px !important;
    padding: 3px;
    min-height: 42px;
    height: auto;
}

.range-btn {
    border: none !important;
    background: transparent !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
    transition: all 0.2s ease !important;
    flex: 1;
    white-space: nowrap;
    min-height: 36px;
    height: auto !important;
    box-shadow: none !important;
}

.range-btn:hover {
    color: #3b4d6b !important;
    background: rgba(0, 0, 0, 0.03) !important;
}

.range-btn.active {
    background: #ffffff !important;
    color: #321fdb !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

::v-deep .dropdown-menu {
  border-radius: 10px !important;
  border: 1px solid #eef0f3 !important;
  box-shadow: 0 8px 24px rgba(149, 157, 165, 0.2) !important;
  padding: 8px !important;
  min-width: 180px !important;
  z-index: 1000 !important;
  transform: none !important;
  top: 100% !important;
  left: 0 !important;
}
</style>
